import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ShoppingCart, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CartDrawer } from '@/components/CartDrawer';
import { useCart } from '@/contexts/CartContext';
import { cn } from '@/lib/utils';

const navLinks = [
  { label: 'Hunting', href: '/hunting' },
  { label: 'Fishing', href: '/fishing' },
  { label: 'Archery', href: '/archery' },
  { label: 'Apparel', href: '/apparel' },
  { label: 'Camping', href: '/camping' },
  { label: 'Blog', href: '/blog' },
  { label: 'About', href: '/about' },
];

export function SiteNav() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { totalItems, setIsOpen: setCartOpen } = useCart();

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 nav-blur bg-[hsl(25,20%,8%)]/90 border-b border-white/10">
        <div className="container mx-auto px-4 flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 sm:gap-3 group" onClick={() => setMobileOpen(false)}>
            <img
              src="/images/DHO.jpg"
              alt="Dakota Horizon Outdoors"
              className="h-9 w-9 sm:h-10 sm:w-10 rounded-full object-cover ring-2 ring-amber-600/60 group-hover:ring-amber-500 transition-all"
            />
            <div className="hidden sm:block">
              <div className="text-white font-bold text-sm leading-tight tracking-wide">DAKOTA HORIZON</div>
              <div className="text-amber-500 text-xs tracking-[0.2em] font-medium">OUTDOORS</div>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={cn(
                  'px-3 py-2 text-sm font-medium tracking-wide transition-colors rounded-sm',
                  location.pathname === link.href
                    ? 'text-amber-400 bg-white/5'
                    : 'text-stone-300 hover:text-amber-400 hover:bg-white/5'
                )}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" className="text-stone-300 hover:text-amber-400 hover:bg-white/10 hidden sm:flex">
              <Search className="h-5 w-5" />
              <span className="sr-only">Search</span>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="text-stone-300 hover:text-amber-400 hover:bg-white/10 relative"
              onClick={() => setCartOpen(true)}
            >
              <ShoppingCart className="h-5 w-5" />
              {totalItems > 0 && (
                <span className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-amber-600 text-white text-[10px] font-bold flex items-center justify-center">
                  {totalItems > 9 ? '9+' : totalItems}
                </span>
              )}
              <span className="sr-only">Cart</span>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden text-stone-300 hover:text-amber-400 hover:bg-white/10"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              <span className="sr-only">Menu</span>
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="lg:hidden bg-[hsl(25,20%,8%)] border-t border-white/10">
            <nav className="container mx-auto px-4 py-3 flex flex-col gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileOpen(false)}
                  className={cn(
                    'px-4 py-3 text-sm font-medium tracking-wide rounded-sm transition-colors',
                    location.pathname === link.href
                      ? 'text-amber-400 bg-white/5'
                      : 'text-stone-300 hover:text-amber-400 hover:bg-white/5'
                  )}
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>
        )}
      </header>

      <CartDrawer />
    </>
  );
}
